import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FLOOR_TYPES } from '@shared/constants/index.ts';
import { TEMPLATE_JOB_TYPES, type TemplateTags } from '@shared/constants/template-taxonomy.ts';
import { Button, Select, TextInput } from './ui.tsx';

/**
 * Room and task picker: a search box, then the buttons that add something.
 *
 * Category, job type and floor are filters for that list. They sit in their
 * own box, as a menu rather than another row of buttons, so they are not
 * mistaken for rooms. The quote's own type decides residential versus
 * commercial — there is no segment filter here.
 */
export interface PickableTemplate {
  id: string;
  categoryCode?: string | null;
  isFavorite?: boolean;
  useCount?: number;
  tags?: TemplateTags | null;
}

function tagList(tags: TemplateTags | null | undefined, key: keyof TemplateTags): readonly string[] {
  return tags?.[key] ?? [];
}

/** Untagged templates stay visible: a tag filter only hides a template that chose other tags. */
function matchesTag(tags: TemplateTags | null | undefined, key: keyof TemplateTags, selected: string): boolean {
  if (selected === 'all') return true;
  const values = tagList(tags, key);
  if (values.length === 0) return true;
  return values.includes(selected);
}

/**
 * Which segments a quote's room and task pickers should list.
 *
 * A commercial quote with repeated units mixes apartment rooms and the
 * building's common areas, so both pickers list residential and commercial.
 * Passing it only on the room picker hides the residential tasks those
 * rooms are made of.
 */
export function segmentsForQuote(segment: string | null | undefined, unitsOn: boolean): readonly string[] | undefined {
  if (unitsOn && segment === 'commercial') return ['residential', 'commercial'];
  return undefined;
}

/**
 * A quote shows the rooms for its own type. A template with no segment tag
 * still shows: an owner-made room that was never tagged is not commercial
 * or residential, and hiding it would take it off every quote.
 */
function matchesSegments(tags: TemplateTags | null | undefined, segments: readonly string[] | null): boolean {
  if (!segments || segments.length === 0) return true;
  const values = tagList(tags, 'segments');
  if (values.length === 0) return true;
  return values.some((segment) => segments.includes(segment));
}

export function TemplatePicker<T extends PickableTemplate>({
  items,
  label,
  categories,
  onPick,
  disabled = false,
  badge,
  nameFor,
  onRemove,
  removeNameFor,
  contextSegment,
  segments,
}: {
  items: readonly T[];
  label: (item: T) => string;
  categories: readonly { code: string; label: string }[];
  onPick: (item: T) => void;
  disabled?: boolean;
  /** Extra text on the button, such as how many are already on the quote. */
  badge?: (item: T) => string | null;
  /** Accessible name when the visible badge is only a count. */
  nameFor?: (item: T) => string | undefined;
  /**
   * Takes one back off. Shown as a minus joined to the button, only while
   * `removeNameFor` names something to take: a room type the quote holds.
   */
  onRemove?: (item: T) => void;
  /** Accessible name for the minus, or null when there is nothing to take off. */
  removeNameFor?: (item: T) => string | null;
  /**
   * The quote's segment. Residential quotes list residential rooms;
   * commercial quotes list commercial rooms. There is no control for this.
   */
  contextSegment?: string | null;
  /**
   * When a commercial quote has turned units on, pass both segments so an
   * apartment's bedrooms and the building's lobby can be added together.
   * Overrides `contextSegment`.
   */
  segments?: readonly string[] | null;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [jobType, setJobType] = useState('all');
  const [floorType, setFloorType] = useState('all');

  const lockedSegments =
    segments && segments.length > 0
      ? segments
      : contextSegment === 'residential' || contextSegment === 'commercial'
        ? [contextSegment]
        : null;

  const tagText = (item: T) =>
    [...tagList(item.tags, 'segments'), ...tagList(item.tags, 'jobTypes'), ...tagList(item.tags, 'floorTypes')].join(
      ' ',
    );

  const needle = query.trim().toLowerCase();
  const matches = (item: T, term: string) => {
    if (!matchesSegments(item.tags, lockedSegments)) return false;
    if (!matchesTag(item.tags, 'jobTypes', jobType)) return false;
    if (!matchesTag(item.tags, 'floorTypes', floorType)) return false;
    if (category === 'favorites' && !item.isFavorite) return false;
    if (category !== 'all' && category !== 'favorites' && (item.categoryCode ?? '') !== category) return false;
    if (!term) return true;
    return `${label(item)} ${tagText(item)}`.toLowerCase().includes(term);
  };

  const filtered = items.filter((item) => matches(item, needle));
  const favorites = filtered.filter((item) => item.isFavorite);
  const common = filtered
    .filter((item) => !item.isFavorite && (item.useCount ?? 0) > 0)
    .sort((left, right) => (right.useCount ?? 0) - (left.useCount ?? 0) || label(left).localeCompare(label(right)));
  const rest = filtered.filter((item) => !item.isFavorite && (item.useCount ?? 0) <= 0);
  const ordered = [...favorites, ...common, ...rest];

  return (
    <div>
      <TextInput label={t('templatePicker.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
      <div
        className="mb-4 rounded-md border border-dashed border-[color:var(--color-line)] px-3 pt-3"
        role="group"
        aria-label={t('templatePicker.filterLabel')}
      >
        <Select
          label={t('templatePicker.filterLabel')}
          hint={t('templatePicker.filterHint')}
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          options={[
            { value: 'all', label: t('templatePicker.all') },
            { value: 'favorites', label: t('templatePicker.favorites') },
            ...categories.map((entry) => ({ value: entry.code, label: entry.label })),
          ]}
        />
        <details className="mb-3">
          <summary className="min-h-11 cursor-pointer text-sm font-semibold">{t('templatePicker.moreFilters')}</summary>
          <div className="grid gap-x-3 sm:grid-cols-2">
            <Select
              label={t('templatePicker.jobType')}
              value={jobType}
              onChange={(event) => setJobType(event.target.value)}
              options={[
                { value: 'all', label: t('templatePicker.anyJobType') },
                ...TEMPLATE_JOB_TYPES.map((type) => ({
                  value: type,
                  label: t(`libraryTaxonomy.jobType.${type}`),
                })),
              ]}
            />
            <Select
              label={t('templatePicker.floor')}
              value={floorType}
              onChange={(event) => setFloorType(event.target.value)}
              options={[
                { value: 'all', label: t('templatePicker.anyFloor') },
                ...FLOOR_TYPES.map((floor) => ({
                  value: floor,
                  label: t(`libraryTaxonomy.floorType.${floor}`),
                })),
              ]}
            />
          </div>
        </details>
      </div>

      {ordered.length === 0 ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('templatePicker.noMatches')}</p>
      ) : (
        <div className="mb-4 flex flex-wrap gap-2">
          {ordered.map((item) => (
            <PickButton
              key={item.id}
              item={item}
              label={label}
              badge={badge}
              nameFor={nameFor}
              disabled={disabled}
              onPick={onPick}
              onRemove={onRemove}
              removeNameFor={removeNameFor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PickButton<T extends PickableTemplate>({
  item,
  label,
  badge,
  nameFor,
  disabled,
  onPick,
  onRemove,
  removeNameFor,
}: {
  item: T;
  label: (item: T) => string;
  badge?: (item: T) => string | null;
  nameFor?: (item: T) => string | undefined;
  disabled: boolean;
  onPick: (item: T) => void;
  onRemove?: (item: T) => void;
  removeNameFor?: (item: T) => string | null;
}) {
  const text = label(item);
  const extra = badge?.(item) ?? null;
  const accessible = nameFor?.(item);
  const removeName = onRemove ? (removeNameFor?.(item) ?? null) : null;
  const main = useRef<HTMLButtonElement>(null);
  /*
    Taking the last one off unmounts the minus while it has focus, which drops
    focus to the page. Put it on the add button beside it instead: the next
    thing a keyboard user is likely to want is that button or the one after.
  */
  const removing = useRef(false);
  useEffect(() => {
    if (!removing.current) return;
    removing.current = false;
    if (!removeName) main.current?.focus();
  }, [removeName]);

  const button = (
    <Button
      ref={main}
      disabled={disabled}
      onClick={() => onPick(item)}
      aria-label={accessible}
      className={removeName ? 'rounded-r-none' : undefined}
    >
      {item.isFavorite ? <span aria-hidden="true">★ </span> : null}
      {text}
      {extra ? (
        <span
          aria-hidden="true"
          className="tabular inline-flex min-w-6 items-center justify-center rounded-full bg-[color:var(--color-brand)] px-1.5 py-0.5 text-xs font-bold text-white"
        >
          {extra}
        </span>
      ) : null}
    </Button>
  );
  if (!onRemove) return button;
  // Wrapped even with no minus showing, so adding the first one does not
  // remount the button that was just pressed and lose its focus.
  return (
    <span className="inline-flex max-w-full">
      {button}
      {removeName ? (
        <Button
          disabled={disabled}
          aria-label={removeName}
          className="-ml-px rounded-l-none px-3"
          onClick={() => {
            removing.current = true;
            onRemove(item);
          }}
        >
          <span aria-hidden="true" className="text-lg leading-none">
            −
          </span>
        </Button>
      ) : null}
    </span>
  );
}
